import { FC, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useMediaQuery } from "context/media/MediaContext";
import { DIGIPOSTHEME } from "theme/digiPosTheme";

import {
  Panel,
  PanelHeader,
  TextField,
  Button,
  Spinner,
  Container,
} from "components";
import {
  Col,
  ElementWrapper,
  Row,
  StyledFormWrapper,
} from "components/BaseComponents/Flex";
import { IFirstPasswordChangeRequest } from "models/Password/password.model";
import { yupResolver } from "@hookform/resolvers/yup";
import { firstPassChangeSubmitValidationSchema } from "validations/password.validation";
import { firstPasswordChangeAsync } from "services/password/password.service";
import { TokenInclude } from "helpers/GetToken";
import usePass from "helpers/hooks/usePass";
import Alert from "components/BaseComponents/Alert/Alert";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { setFirstPassChange } from "redux/slices/password-slices/PasswordRefreshSlice";
import { ResetLogin, setFirstPassUserName } from "redux/slices/LoginSlice";
import { resetOtp } from "redux/slices/OtpSlice";
import { MerchantAuthSuccess } from "services/merchantSwitch.service";
import { setMerchantAuthSuccess } from "redux/slices/MerchantSwitchSlice";
import { requestDataFirstChange } from "./PassForm.constant";
import { resetServiceError } from "redux/slices/ServiceInlineError";
import FirstPasswordChangeExitModal from "./FirstPasswordChangeExitModal";

const FirstPasswordChange: FC<{}> = () => {
  const { t } = useTranslation();
  const { isMobile } = useMediaQuery();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const token = TokenInclude();
  const IsPasswordChange = usePass();

  const [isAddSuccess, setIsAddSuccess] = useState<boolean>(false);
  const [isAddError, setIsAddError] = useState<boolean>(false);
  const [txt, setTxt] = useState<string>(t("success_add_message").toString());
  const [openModal, setOpenModal] = useState<boolean>(false);

  const { user } = useAppSelector((state) => state.login);
  const { userOtp } = useAppSelector((state) => state.otp);
  const { firstPassChange } = useAppSelector(
    (state) => state.PassRefPersonelInfo
  );
  const requestData: IFirstPasswordChangeRequest = {
    name: user?.Name,
    surname: user?.Surname,
  };

  const defaultValuesFirst = { ...requestData, ...requestDataFirstChange };

  const {
    handleSubmit,
    control,
    errors,
    reset,
    formState: { isSubmitting },
  } = useForm<IFirstPasswordChangeRequest>({
    defaultValues: defaultValuesFirst,
    resolver: yupResolver(firstPassChangeSubmitValidationSchema(t)),
  });
  const onSubmit = async (data: IFirstPasswordChangeRequest): Promise<void> => {
    const { name, surname, newPassword } = data;

    const retVal = await firstPasswordChangeAsync(
      token,
      name!,
      surname!,
      newPassword!
    );
    if (retVal.Confirm) {
      // dispatch(ResetLogin());
      // dispatch(resetOtp());
      // dispatch(resetServiceError());
      // navigate("/");
      setOpenModal((prev) => !prev);

      // const returnMerhanchSuccess = await MerchantAuthSuccess(
      //   token.TokenSessionId
      // );
      // if (returnMerhanchSuccess?.DialogBoxes?.length < 1) {
      //   const _user = { ...user };
      //   _user.Name = name!;
      //   _user.Surname = surname!;
      //   _user.adSoyad = `${name} ${surname}`;
      //   dispatch(setFirstPassUserName(_user));

      //   dispatch(setMerchantAuthSuccess(returnMerhanchSuccess));
      //   setIsAddSuccess(true);
      //   dispatch(setFirstPassChange(true));
      // } else {
      //   const errMessage: any = retVal;
      //   setIsAddError(true);
      //   setTxt(errMessage);
      //   return;
      // }
    } else {
      const errMessage: any = retVal;
      setIsAddError(true);
      setTxt(errMessage);
      return;
    }
    reset();
  };

  useEffect(() => {
    if (IsPasswordChange && firstPassChange) {
      setTimeout(() => {
        navigate("/");
      }, 3000);
    }
  }, [IsPasswordChange, firstPassChange, navigate]);

  const NavigateBackPage = () => {
    dispatch(ResetLogin());
    dispatch(resetOtp());
    dispatch(setFirstPassChange(false));
    navigate("/");
  };

  return (
    <Container>
      <ElementWrapper
        minHeight={isMobile ? "calc(100vh - 118px)" : "calc(100vh - 137px)"}
        padding='48px 0 0 0'
        width='872px'
      >
        <Panel width='646px' marginTop='0'>
          {(isAddSuccess || isAddError) && (
            <Alert
              alertType={isAddSuccess ? "Success" : "Error"}
              duration={6000}
              isVisible={isAddSuccess || isAddError}
              message={txt}
              setIsAddSuccess={setIsAddSuccess}
              setIsAddError={setIsAddError}
            />
          )}
          <Spinner show={isSubmitting} />
          <PanelHeader title={t("change_passsword_enter_password")} />

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <>
              {userOtp?.UserLevel === "2" && (
                <StyledFormWrapper>
                  <Row
                    alignItems='flex-start'
                    direction={isMobile ? "column-reverse" : "row"}
                    noMargin
                  >
                    <Row width='100%'>
                      <Col>
                        <TextField
                          control={control}
                          label={t("name").toString()}
                          name='name'
                          type='alphabetic'
                        />
                      </Col>
                    </Row>
                    <Row width='100%'>
                      <Col>
                        <TextField
                          control={control}
                          label={t("surname").toString()}
                          name='surname'
                          type='alphabetic'
                        />
                      </Col>
                    </Row>
                  </Row>
                </StyledFormWrapper>
              )}
            </>

            <StyledFormWrapper>
              <PanelHeader />
              <Row
                alignItems='flex-start'
                direction={isMobile ? "column-reverse" : "row"}
                noMargin
              >
                <Row width='100%'>
                  <Col>
                    <TextField
                      control={control}
                      error={errors.newPassword?.message}
                      label={t("new_password").toString()}
                      maxLength={6}
                      name='newPassword'
                      type='masked'
                    />
                  </Col>
                </Row>
                <Row width='100%'>
                  <Col>
                    <TextField
                      control={control}
                      error={errors.newPasswordAgain?.message}
                      label={t("new_password_repeat").toString()}
                      maxLength={6}
                      name='newPasswordAgain'
                      type='masked'
                    />
                  </Col>
                </Row>
              </Row>
            </StyledFormWrapper>

            <Row
              backgroundColor={DIGIPOSTHEME.colors.secondaryOffWhite}
              justifyContent='flex-end'
              isResponsive
              noMargin
            >
              <Col>
                <Button
                  type='button'
                  width={isMobile ? "100%" : "100px"}
                  text={`← ${t("back")}`}
                  onClick={NavigateBackPage}
                  color='light'
                  disabled={isAddSuccess}
                />
              </Col>
              <Col offSet={22} size={0.5}>
                <Button text={t("continue").toString()} type='submit' />
              </Col>
            </Row>
          </form>

          {openModal && (
            <FirstPasswordChangeExitModal
              acceptButtonText={t("ok").toString()}
              message={t("new_pass_relogin").toString()}
              setIsOpen={setOpenModal}
              title={t("information").toString()}
            />
          )}
        </Panel>
      </ElementWrapper>
    </Container>
  );
};

export default FirstPasswordChange;
